import React, { memo, useEffect, useState, useMemo, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import Modal from '../../components/shared/modal';
import { useTranslation } from "react-i18next";
import * as dayjs from 'dayjs';

import { organizationInvitesList, organizationMembersList, removeOrganizationMember, removeOrganizationInvite, organizationInvite } from "../../services/organizations";

import Table from "../../components/Table";
import { SearchBar } from "../../components/FilterBar";

const typeConfig = {
    users: {
        type: 'users',
        dateField: 'last_seen_at',
        dateLabel: 'table_last',
        fetch: organizationMembersList,
        remove: (user) => removeOrganizationMember(user.id),
    },
    invites: {
        type: 'invites',
        dateField: 'created_at',
        dateLabel: 'table_invite',
        fetch: organizationInvitesList,
        remove: (invite) => removeOrganizationInvite(invite.id),
    }
};

const MembersPage = () => {
    const { t } = useTranslation();
    const dataTableRef = useRef(null);
    const [userType, setUserType] = useState('users');
    const [{ users_count, invites_count }, setUsersAmount] = useState({});

    const getUsersAmount = async () => {
        const [users, invites] = await Promise.all([
            organizationMembersList({ count: true }),
            organizationInvitesList({ count: true })
        ]);
        setUsersAmount({ users_count: users.count, invites_count: invites.count });
    };

    useEffect(() => { getUsersAmount(); }, []);

    const reloadSubData = useCallback(() => {
        getUsersAmount();
        dataTableRef.current?.getData();
    }, []);

    return (
        <>
            <div className="team-details">
                <div className="team-details-header">
                    <div className="team-details-header-text">{t('common.members')}</div>
                    <div className="team-details-header-btn">
                        <InviteOrganizationMemberButton onClose={(reload) => reload && reloadSubData()} />
                    </div>
                </div>
                <div className="team-details-tabs">
                    <div className={`team-details-tabs-option pointer ${userType === 'users' ? 'active' : ''}`} onClick={() => setUserType('users')}>{t('organization.members.users', { count: users_count })}</div>
                    <div className={`team-details-tabs-option pointer ${userType === 'invites' ? 'active' : ''}`} onClick={() => setUserType('invites')}>{t('organization.members.invites', { count: invites_count })}</div>
                </div>

                <div className="team-details-list">
                    <DataTable
                        ref={dataTableRef}
                        config={{ ...typeConfig[userType] }}
                        onDataChange={reloadSubData}
                    />
                </div>
            </div>
        </>
    );
};

export default memo(MembersPage);

const DataTable = forwardRef(({ config, onDataChange }, ref) => {
    const { t } = useTranslation();
    const { type, dateField, dateLabel, fetch, remove } = config;
    const [{ items, count }, setItems] = useState({ items: [], count: 0 });
    const [filters, setFilters] = useState({ page: 1, per_page: 10, q: "" });
    const { page, per_page } = filters;

    useImperativeHandle(ref, () => ({ getData }));

    const getData = async () => {
        const res = await fetch(filters);
        setItems({ items: res[type] || [], count: res.count || 0 });
    };

    useEffect(() => { getData() }, [filters]);
    useEffect(() => { setFilters(prev => ({ ...prev, page: 1 })) }, [type]);

    const pagination = useMemo(() => ({
        pageIndex: page - 1,
        pageSize: per_page,
    }), [page, per_page]);

    const handlePagination = (state) => {
        const pageIndex = state?.pageIndex ?? 0;
        const pageSize = state?.pageSize ?? per_page;
        setFilters(prev => ({ ...prev, page: pageIndex + 1, per_page: pageSize }));
    };

    const removeEntry = async (entry) => {
        await remove(entry);
        getData();
        onDataChange();
    };

    const columns = useMemo(() => [
        { header: t('common.table_name'), accessorKey: 'name', cell: info => <div>{info.getValue()}</div> },
        { header: t('common.table_email'), accessorKey: 'email', cell: info => info.getValue() },
        { header: t(`coaches.${dateLabel}`), accessorKey: dateField, cell: info => info.getValue() ? dayjs(info.getValue()).format('YYYY-MM-DD') : '-' },
        {
            id: 'actions', header: ' ',
            cell: info =>
                <i className="icon icon-trash"
                    onClick={() => confirm(t(`coaches.confirm_remove_${type === 'users' ? 'user' : 'invite'}`, { name: info.row.original?.email })) && removeEntry(info.row.original)}></i>,
            meta: { style: { width: 60 } }
        }
    ], [type, t]);

    return (
        <>
            <div className='filters-bar'>
                <SearchBar onChange={(q) => setFilters(prev => ({ ...prev, q, page: 1 }))} placeholder={t('lessons.placeholder')} />
            </div>

            <Table {...{ data: items, columns, pageCount: Math.ceil(count / per_page), manualPagination: true, onPaginationChange: (e) => handlePagination(e(pagination)) }} />
        </>
    );
});


const InviteOrganizationMemberButton = ({ onClose }) => {
    const [errors, setErrors] = useState([]);
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [invite, setInvite] = useState({ email: '', name: '' });
    const { t } = useTranslation();

    const open = () => {
        setInvite({ email: '', name: '' });
        setShow(true);
    };

    const save = async () => {
        setLoading(true);
        setErrors([]);
        try {
            await organizationInvite({ ...invite });
            close(true);
        } catch (e) {
            if (e?.response?.data?.errors) setErrors(e.response.data.errors);
            else setErrors([e.message || 'Unknown error']);
        }
        setLoading(false);
    };

    const close = (reload) => {
        setShow(false);
        setInvite({ email: '', name: '' });
        onClose && onClose(reload);
    };

    return (
        <div>
            <button className="button button-add" onClick={open}>{t('buttons.common.invite-member')}</button>
            <Modal openModal={show} closeModal={() => setShow(false)}>
                <div className="dialog-content invite-dialog">
                    <div>
                        <div className='form-group'>
                            <label htmlFor="name">{t('common.table_name')}</label>
                            <input id="name" type="text" value={invite.name} onChange={e => setInvite(prev => ({ ...prev, name: e.target.value }))} />
                        </div>

                        <div className='form-group'>
                            <label htmlFor="email">{t('common.table_email')}</label>
                            <input id="email" type="text" value={invite.email} required onChange={e => setInvite(prev => ({ ...prev, email: e.target.value }))} />
                        </div>
                    </div>
                </div>

                <div>
                    {errors.map((message, idx) => <p key={idx} style={{ fontSize: 12 }}>{message}</p>)}
                </div>

                <div className='form-container-actions'>
                    <div className='flex-end'>
                        <button className="button" disabled={loading} onClick={() => close(false)}>{t('buttons.common.cancel')}</button>
                        <button className="button button-primary" disabled={loading} onClick={() => save()}>{t('buttons.common.invite')}</button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};